/**
 * Server-resolved cover treatment for Browse Topic cards.
 *
 * This is deliberately an optional display hint rather than a ranking input:
 * callers must not derive it from a personalized queue.
 */
export const BROWSE_COVER_BADGES = ['github', 'trending', 'new'] as const;

export type BrowseCoverBadge = (typeof BROWSE_COVER_BADGES)[number];

/** Display label per badge, shared by Topic cards and the Topic hero badge. */
export const BROWSE_COVER_BADGE_LABELS: Record<BrowseCoverBadge, string> = {
	github: 'GitHub',
	trending: 'Trending',
	new: 'New',
};

/** Badge attributes the resolver needs. Both fields are optional so private
 *  or legacy records simply resolve to no badge. */
export type TopicCoverBadgeInput = {
	source?: string | null;
	createdAt?: string | null;
};

const NEW_BADGE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Deterministic badge priority shared by every surface that shows a Topic
 * cover badge: GitHub source first, then recency. Trending is deliberately
 * NOT resolved here — it depends on the global ranking snapshot, which only
 * the server badge service reads.
 */
export function resolveTopicCoverBadge(topic: TopicCoverBadgeInput, now: Date): Exclude<BrowseCoverBadge, 'trending'> | undefined {
	if (topic.source === 'github_trending') return 'github';
	if (isCreatedWithinNewBadgeWindow(topic.createdAt, now)) return 'new';
	return undefined;
}

function isCreatedWithinNewBadgeWindow(createdAt: string | null | undefined, now: Date): boolean {
	if (!createdAt) return false;
	const createdAtMs = Date.parse(createdAt);
	if (!Number.isFinite(createdAtMs)) return false;
	return createdAtMs <= now.getTime() && createdAtMs >= now.getTime() - NEW_BADGE_WINDOW_MS;
}
